// Auth context (BUILD_SPEC.md §6, §11; plan.md G18). Two login paths behind one
// context: a Cognito hosted-UI redirect (real path, once infra/G27 exists) and a
// dev-mode fallback that accepts a pasted self-signed JWT (services/api
// AUTH_MODE=dev) + a tenant id, gated behind VITE_ENABLE_DEV_AUTH so it can never be
// mistaken for the real thing. Either way, the RESULT is the same shape this
// context publishes: a bearer token + an active tenant id + role, which is exactly
// what src/api/client.ts needs to attach `Authorization`/`X-Tenant-Id` to every
// request (services/api's middleware/auth.ts contract).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Role, TenantSettings } from '@zebra/core';
import { apiFetch, ApiRequestError, configureApiAuth } from '../api/client';
import { clearAuthState, loadAuthState, saveAuthState } from './storage';
import { resetSessionExpiryLatch } from './sessionExpiry';
import type { AuthContextValue, TenantMembership } from './types';

const AuthReactContext = createContext<AuthContextValue | null>(null);

/** Backoff schedule for refreshMemberships' cold-start retries, in ms between
 * attempts (index 0 = delay before the 2nd attempt, etc.) — three retries
 * after the initial immediate one, four attempts total. Aurora Serverless v2
 * (min 0 ACU, 10-min auto-pause) can take 15-30s to resume the DB on the
 * first request after idle, and API Gateway's 29s cap means that first
 * request can time out outright rather than just being slow — increasing
 * delays give the DB room to finish resuming rather than hammering it with a
 * tight retry loop while it's still coming up. */
const REFRESH_RETRY_DELAYS_MS = [2000, 5000, 8000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Mirrors services/api/src/me.ts's `MeResponse` (the seam contract that file's
 * header names) — apps/web has no build-time dependency on services/api, so this
 * is a local, independently-maintained copy of the shape, not an import. */
interface MeResponse {
  user: { id: string; email: string; name: string };
  // `tenantSettings` optional here (unlike the server's own MeResponse, where it's
  // required): older/mocked responses in tests predate this field, and the mapping
  // below defaults a missing value to `{}` — same "absent = every gate off" read
  // the server itself falls back to on an unparseable settings blob.
  memberships: Array<{ tenantId: string; tenantName: string; role: Role; tenantSettings?: TenantSettings }>;
}

/** Decodes a JWT's payload WITHOUT verifying the signature — display purposes only
 * (e.g. showing the signed-in email in the top bar). Every real authorization
 * decision is made server-side by services/api's middleware, which does verify the
 * signature (aws-jwt-verify in Cognito mode, HMAC in dev mode). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(normalized);
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = useMemo(() => loadAuthState(), []);
  const [token, setToken] = useState<string | null>(initial.token);
  const [tenants, setTenants] = useState<TenantMembership[]>(initial.tenants);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(initial.activeTenantId);
  const [membershipsStatus, setMembershipsStatus] = useState<AuthContextValue['membershipsStatus']>('idle');
  // Guards against overlapping refreshMemberships() calls (e.g. the login page's
  // mount effect firing while a manual Retry click is still in flight).
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    saveAuthState({ token, tenants, activeTenantId });
  }, [token, tenants, activeTenantId]);

  // Re-arms sessionExpiry's redirect-once latch on every successful sign-in
  // (fresh Cognito callback, dev login, or a persisted session reloading with a
  // token already present) — otherwise a SECOND dead token later in the same tab
  // would silently never redirect again, since the latch is module state that
  // outlives this component's re-renders.
  useEffect(() => {
    if (token) resetSessionExpiryLatch();
  }, [token]);

  // The seam into src/api/client.ts (a plain module, not a React context) — every
  // request reads auth headers through this ref, always current.
  //
  // CRITICAL: the ref is updated DURING RENDER and the provider is wired during
  // the FIRST render — never via an effect keyed on [token]. React runs effects
  // child-first, parent-last: on a fresh sign-in, login.tsx's fetch effect (a
  // child) fires in the same commit that delivers the new token, BEFORE any
  // AuthProvider effect has run — an effect-synced closure still held
  // token=null at that instant, so the very first GET /me went out with NO
  // Authorization header and 401'd on every fresh sign-in, on every device
  // (prod incident 2026-08-25 22:10: access log shows the 401s; the Lambda log
  // shows no token-verification failure — no bearer was ever sent).
  const headerSourceRef = useRef<{ token: string | null; tenantId: string | null }>({
    token: null,
    tenantId: null,
  });
  headerSourceRef.current = { token, tenantId: activeTenantId };
  const apiAuthWiredRef = useRef(false);
  if (!apiAuthWiredRef.current) {
    apiAuthWiredRef.current = true;
    configureApiAuth(() => headerSourceRef.current);
  }

  const activeTenant = useMemo(
    () => tenants.find((t) => t.tenantId === activeTenantId) ?? null,
    [tenants, activeTenantId],
  );

  const userEmail = useMemo(() => {
    if (!token) return null;
    const email = decodeJwtPayload(token)?.email;
    return typeof email === 'string' ? email : null;
  }, [token]);

  const devAuthEnabled = import.meta.env.VITE_ENABLE_DEV_AUTH === 'true';

  const addTenant = useCallback((input: { tenantId: string; label: string; role: Role }) => {
    // Dev-mode/manual tenant add has no real settings to report — `{}` (every
    // gated feature off) matches how the server itself treats an unset key.
    setTenants((prev) => [...prev.filter((t) => t.tenantId !== input.tenantId), { ...input, settings: {} }]);
    setActiveTenantId(input.tenantId);
  }, []);

  const loginDev = useCallback(
    (input: { token: string; tenantId: string; label: string; role: Role }) => {
      setToken(input.token);
      addTenant({ tenantId: input.tenantId, label: input.label, role: input.role });
    },
    [addTenant],
  );

  const beginCognitoLogin = useCallback(() => {
    const domain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    if (!domain || !clientId) {
      throw new Error(
        'Cognito is not configured for this deployment (VITE_COGNITO_DOMAIN / VITE_COGNITO_CLIENT_ID missing).',
      );
    }
    const redirectUri = import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/auth-callback`;
    // /oauth2/authorize (not /login) + identity_provider=MicrosoftEntra: Cognito
    // skips its own hosted-UI account-chooser page entirely and redirects
    // straight to Microsoft (2026-08-22 ruling — Microsoft-only sign-in, see
    // infra/lib/auth-stack.ts's `MicrosoftEntra` identity provider, name kept in
    // sync with this string by comment contract on both sides). Everything past
    // this point is unchanged: Microsoft authenticates the user, Cognito mints
    // its own id token from the federated identity, and redirects back here via
    // the same implicit-grant URL-fragment contract completeCognitoCallback reads.
    const hostedUiUrl = new URL(`https://${domain}/oauth2/authorize`);
    hostedUiUrl.searchParams.set('client_id', clientId);
    hostedUiUrl.searchParams.set('response_type', 'token');
    hostedUiUrl.searchParams.set('scope', 'openid email profile');
    hostedUiUrl.searchParams.set('redirect_uri', redirectUri);
    hostedUiUrl.searchParams.set('identity_provider', 'MicrosoftEntra');
    window.location.assign(hostedUiUrl.toString());
  }, []);

  const completeCognitoCallback = useCallback((): boolean => {
    const rawHash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(rawHash);
    const idToken = params.get('id_token');
    // Strip the token out of the visible URL immediately either way.
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    if (!idToken) return false;
    setToken(idToken);
    return true;
  }, []);

  const switchTenant = useCallback((tenantId: string) => {
    setActiveTenantId(tenantId);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setTenants([]);
    setActiveTenantId(null);
    setMembershipsStatus('idle');
    clearAuthState();
  }, []);

  // Server-driven tenant chooser (orchestrator ruling, 2026-08-24): fetches GET
  // /me (bearer-only, no X-Tenant-Id — services/api/src/me.ts) and replaces
  // `tenants` wholesale, since the server is authoritative for both which
  // tenants exist and which role the user holds in each — a client-side merge
  // would let a revoked membership linger in the UI until logout.
  const refreshMemberships = useCallback(async (): Promise<void> => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    // Captured at call-start, not read again after the await: "auto-select the
    // one membership" only makes sense for a chooser that had nothing selected
    // yet, not for the deliberate "Manage tenants…" revisit of an already-active
    // session (login.tsx tracks the same condition for its auto-navigate).
    const hadNoActiveTenant = activeTenantId === null;
    setMembershipsStatus('loading');
    try {
      // Cold-start retry loop (see REFRESH_RETRY_DELAYS_MS's comment). This
      // whole loop — not just the first attempt — is the ONE in-flight
      // refresh: refreshInFlightRef stays true across every attempt (it's
      // only reset in the outer `finally` below), so a concurrent call made
      // while a backoff delay is in progress (e.g. login.tsx's mount effect
      // racing a user's manual Retry click) is a correct no-op rather than a
      // second parallel request pair. membershipsStatus is left at 'loading'
      // for the whole loop too, on purpose — login.tsx's slow-load hint reads
      // that status, not attempt count, and should keep showing through
      // every retry, not flicker per attempt.
      let lastError: unknown;
      for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
        if (attempt > 0) {
          await sleep(REFRESH_RETRY_DELAYS_MS[attempt - 1]!);
        }
        // Captured per attempt: apiFetch reads the CURRENT token via
        // headerSourceRef, so this is exactly the token the request carries.
        const tokenForAttempt = headerSourceRef.current.token;
        try {
          const response = await apiFetch<MeResponse>('/me');
          const fetched: TenantMembership[] = response.memberships.map((m) => ({
            tenantId: m.tenantId,
            label: m.tenantName,
            role: m.role,
            settings: m.tenantSettings ?? {},
          }));
          setTenants(fetched);
          setActiveTenantId((current) => {
            if (hadNoActiveTenant && fetched.length === 1) return fetched[0]!.tenantId;
            if (current && !fetched.some((t) => t.tenantId === current)) return null;
            return current;
          });
          setMembershipsStatus('ready');
          return;
        } catch (cause) {
          if (cause instanceof ApiRequestError && cause.status === 401) {
            // Expired/invalid token — no amount of retrying fixes this; send
            // the user back through sign-in rather than surfacing a dead-end
            // 'error' (or worse, retrying it for 15s first). But ONLY if the
            // rejected token is still the current one: a slow first attempt
            // (paused-Aurora cold start, 12s+) can come back 401 AFTER a newer
            // sign-in has already succeeded — logging out then tears down the
            // fresh session (observed prod 2026-08-25 ~14:03 and ~18:06: a
            // 12.5s cold 401 landed the same second as a newer attempt's 200
            // and bounced the user straight back to the sign-in card).
            if (headerSourceRef.current.token === tokenForAttempt) {
              logout();
            }
            return;
          }
          lastError = cause;
        }
      }
      console.error('refreshMemberships: gave up after retries', lastError);
      setMembershipsStatus('error');
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [activeTenantId, logout]);

  const value: AuthContextValue = {
    isAuthenticated: Boolean(token && activeTenantId),
    token,
    userEmail,
    tenants,
    activeTenant,
    membershipsStatus,
    devAuthEnabled,
    loginDev,
    beginCognitoLogin,
    completeCognitoCallback,
    refreshMemberships,
    addTenant,
    switchTenant,
    logout,
  };

  return <AuthReactContext.Provider value={value}>{children}</AuthReactContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthReactContext);
  if (!ctx) throw new Error('useAuth() must be called within <AuthProvider>');
  return ctx;
}
